import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';

import { messagingService } from '../../../../services/messaging.service';
import { theme } from '../../../../theme';

const REQUEST_KINDS = [
  ['information', 'Une information'],
  ['new_appointment', 'Un nouveau rendez-vous'],
  ['existing_appointment', 'Mon rendez-vous'],
  ['other', 'Autre'],
];

const ClientChatStarter = ({
  salon,
  appointments = [],
  onCreated,
}) => {
  const [kind, setKind] = useState('information');
  const [appointmentId, setAppointmentId] = useState('');
  const [subjectOpen, setSubjectOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const salonAppointments = useMemo(() => appointments.filter((item) => (
    Number(item.salon?.salon_id ?? item.salon)
    === Number(salon?.salon_id)
  )), [appointments, salon?.salon_id]);

  const disabled = (
    busy
    || !message.trim()
    || (kind === 'existing_appointment' && !appointmentId)
  );

  const submit = async () => {
    if (disabled || !salon?.salon_id) return;
    setBusy(true);
    setError('');

    try {
      const response = await messagingService.createClientConversation({
        salon_id: Number(salon.salon_id),
        kind,
        appointment_id: kind === 'existing_appointment'
          ? Number(appointmentId)
          : null,
        subject: subject.trim(),
        initial_message: message.trim(),
      });
      onCreated?.(response.data);
    } catch (requestError) {
      if (requestError?.response?.status === 409) {
        onCreated?.({
          id: requestError.response.data.conversation_id,
          existing: true,
        });
      } else {
        setError('Impossible d’envoyer le message.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        <Paper
          elevation={0}
          sx={{
            display: 'inline-block',
            maxWidth: '85%',
            p: 1.5,
            backgroundColor: theme.colors.secondary[100],
            borderRadius: '16px 16px 16px 4px',
          }}
        >
          <Typography variant="body2">
            Bonjour ! Comment pouvons-nous vous aider ?
          </Typography>
        </Paper>

        <Typography
          variant="body2"
          sx={{ mt: 3, mb: 1, color: theme.colors.grey[700] }}
        >
          Choisissez un motif
        </Typography>
        <Stack spacing={1}>
          {REQUEST_KINDS.map(([value, label]) => (
            <Button
              key={value}
              color="inherit"
              variant={kind === value ? 'contained' : 'outlined'}
              onClick={() => {
                setKind(value);
                setAppointmentId('');
              }}
              sx={{
                justifyContent: 'flex-start',
                borderRadius: 2.5,
                borderColor: theme.colors.primary[200],
                color: theme.colors.grey[900],
                textTransform: 'none',
                boxShadow: 'none',
                '&.MuiButton-contained': {
                  backgroundColor: theme.colors.primary[100],
                  color: theme.colors.primary[900],
                },
                '&:hover': {
                  borderColor: theme.colors.primary[400],
                  backgroundColor: theme.colors.primary[50],
                  boxShadow: 'none',
                },
              }}
            >
              {label}
            </Button>
          ))}
        </Stack>

        {kind === 'existing_appointment' && (
          <TextField
            fullWidth
            select
            SelectProps={{ native: true }}
            label="Rendez-vous concerné"
            value={appointmentId}
            onChange={(event) => setAppointmentId(event.target.value)}
            sx={{ mt: 2 }}
          >
            <option value="" />
            {salonAppointments.map((item) => (
              <option
                key={item.appointment_id}
                value={item.appointment_id}
              >
                {item.service_name || `#${item.appointment_id}`} —{' '}
                {new Date(item.start_time).toLocaleString()}
              </option>
            ))}
          </TextField>
        )}

        <Button
          color="inherit"
          variant="text"
          onClick={() => setSubjectOpen((value) => !value)}
          sx={{
            mt: 1.5,
            px: 0,
            color: theme.colors.primary[800],
            textTransform: 'none',
          }}
        >
          {subjectOpen ? 'Masquer le sujet' : 'Ajouter un sujet'}
        </Button>
        <Collapse in={subjectOpen}>
          <TextField
            fullWidth
            size="small"
            label="Sujet (facultatif)"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            inputProps={{ maxLength: 160 }}
            sx={{ mt: 1 }}
          />
        </Collapse>
      </Box>

      <Box
        sx={{
          p: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.82)',
          borderTop: `1px solid ${theme.colors.primary[100]}`,
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={5}
            placeholder="Écrivez votre message…"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            inputProps={{ maxLength: 4000 }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: theme.colors.secondary[50],
              },
            }}
          />
          <IconButton
            aria-label="Envoyer"
            onClick={submit}
            disabled={disabled}
            sx={{
              width: 46,
              height: 46,
              backgroundColor: theme.colors.primary[800],
              color: 'white',
              '&:hover': {
                backgroundColor: theme.colors.primary[900],
              },
              '&.Mui-disabled': {
                backgroundColor: theme.colors.grey[300],
                color: theme.colors.grey[500],
              },
            }}
          >
            {busy ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SendOutlinedIcon />
            )}
          </IconButton>
        </Box>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 1,
            color: theme.colors.grey[600],
          }}
        >
          Votre message sera envoyé au salon.
        </Typography>
      </Box>
    </Box>
  );
};

export default ClientChatStarter;

